import {
	Card,
	LoadingDots,
	MuiAlert,
	Paper,
	ResponsiveDrawer,
	strIsNullOrEmpty,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography
} from '@fox/atoms';
import {
	DeviceType,
	FlexColumnMiniSpaced,
	FlexColumnSmallSpaced,
	FlexColumnSpaced,
	FlexRowCenterJustify,
	FlexRowSpaced,
	Padded,
	useDeviceType
} from '@fox/layout';
import Pagination from '@mui/material/Pagination';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { useComponentStyleContext } from '../../component-style-context';
import { useContentContext } from '../../content-context';
import { PaginationWrapper } from '../../NewsList/index';
import { useActionCentreContext } from './action-centre-context';
import { useActionCentre } from './action-centre-data';
import { ActionCentreColumn, CardViewSettings, RenderType } from './contracts';

// Field renderer function for different render types
const renderFieldValue = (value: string, renderType?: RenderType) => {
	if (!value || value === '-') return '-';

	switch (renderType) {
		case 'hyperlink':
			return (
				<a
					href={value}
					target="_blank"
					rel="noopener noreferrer"
					onClick={e => e.stopPropagation()}
					style={{
						color: 'inherit',
						textDecoration: 'underline',
						fontSize: 'inherit',
						fontFamily: 'inherit'
					}}
				>
					view
				</a>
			);
		case 'image':
			return (
				<img
					src={value}
					alt=""
					style={{
						maxWidth: '100%',
						height: 'auto',
						objectFit: 'cover'
					}}
				/>
			);

		default:
			return value;
	}
};

export function ActionCentreCards({
	actionId,
	actionCentreColumns,
	pageSize,
	cardViewSettings
}: {
	actionId: string;
	actionCentreColumns: ActionCentreColumn[];
	pageSize: number;
	cardViewSettings: CardViewSettings | undefined;
}) {
	const { contentId, requestToken } = useContentContext();
	const { filters, page, sourceFilter, setPage } = useActionCentreContext();
	const deviceType = useDeviceType();

	const { data, error, loading } = useActionCentre(
		{
			contentId,
			id: actionId,
			page: page - 1,
			pageSize,
			sourceFilter,
			filters
		},
		requestToken
	);

	if (!strIsNullOrEmpty(error)) return <MuiAlert severity="error">{error}</MuiAlert>;
	if (loading)
		return (
			<FlexRowCenterJustify>
				<LoadingDots style={{ maxWidth: '2000px' }} />
			</FlexRowCenterJustify>
		);
	if (!data) return null;
	if (data.totalResults < 1)
		return (
			<FlexRowCenterJustify>
				<MuiAlert sx={{ maxWidth: '2000px' }} severity="info">
					No data to display
				</MuiAlert>
			</FlexRowCenterJustify>
		);
	return (
		<FlexColumnSmallSpaced>
			<ResponsiveCardWrapper $deviceType={deviceType} $hasThumbnailField={!!cardViewSettings?.thumbnailField}>
				<ActionCardsWrapper $hasThumbnailField={!!cardViewSettings?.thumbnailField}>
					{data?.data.map((d, i) => {
						return <ActionCard key={i} data={d} columns={actionCentreColumns} cardViewSettings={cardViewSettings} />;
					})}
				</ActionCardsWrapper>
			</ResponsiveCardWrapper>
			{data.totalResults <= pageSize ? null : (
				<PaginationWrapper>
					<Pagination
						style={{ alignSelf: 'center' }}
						count={Math.ceil(data.totalResults / pageSize)}
						page={page}
						onChange={(_, value) => {
							setPage(value);
						}}
						color={'secondary'}
					/>
				</PaginationWrapper>
			)}
		</FlexColumnSmallSpaced>
	);
}

function ActionCard({
	data,
	columns,
	cardViewSettings
}: {
	data: Record<string, string>;
	columns: ActionCentreColumn[];
	cardViewSettings?: CardViewSettings;
}) {
	const [open, setOpen] = useState(false);
	const deviceType = useDeviceType();
	const {
		cardBackgroundColour,
		cardBorderColour,
		cardBorderRadiusPx,
		cardBorderStyle,
		cardBorderWidthPx,
		cardBoxShadowStyle,
		cardFont,
		cardFontColour,
		cardHeaderFont,
		cardHeaderFontColour
	} = useComponentStyleContext();

	// Build title from titleFields (space separated)
	const titleValue =
		cardViewSettings?.titleFields
			?.map(titleField => data[titleField.columnId])
			.filter(value => value && value.trim() !== '')
			.join(' ') || 'Item Details';

	// Use drawerTitle if provided, otherwise fall back to titleValue
	const drawerTitleValue = cardViewSettings?.drawerTitle || titleValue;

	// Get thumbnail value if thumbnailField is specified, with fallback
	const thumbnailValue = cardViewSettings?.thumbnailField ? data[cardViewSettings.thumbnailField] || '/img/default-fallback.webp' : null;

	// Get card fields to display on the card
	const cardFieldsToDisplay =
		cardViewSettings?.cardFields
			?.map(cardField => {
				const column = columns.find(col => col.id === cardField.columnId);
				return column ? { column, value: data[cardField.columnId], cardField } : null;
			})
			.filter(Boolean) || [];

	// Get drawer fields to display - if no drawerFields specified, show all columns
	const drawerFieldsToDisplay = cardViewSettings?.drawerFields?.length
		? cardViewSettings.drawerFields
				.map(drawerField => {
					const column = columns.find(col => col.id === drawerField.columnId);
					return column ? { column, value: data[drawerField.columnId], drawerField } : null;
				})
				.filter(Boolean)
		: columns.map(column => ({
				column,
				value: data[column.id],
				drawerField: { columnId: column.id } as any
		  }));

	// Group drawer fields by fieldGroup
	const groupedDrawerFields = React.useMemo(() => {
		const groups: Record<string, typeof drawerFieldsToDisplay> = {};
		const ungroupedFields: typeof drawerFieldsToDisplay = [];

		drawerFieldsToDisplay.forEach(field => {
			const groupName = field.drawerField?.fieldGroup;
			if (groupName) {
				if (!groups[groupName]) {
					groups[groupName] = [];
				}
				groups[groupName].push(field);
			} else {
				ungroupedFields.push(field);
			}
		});

		return { groups, ungroupedFields };
	}, [drawerFieldsToDisplay]);

	// Generate card styles from context
	const cardStyles = {
		...cardBackgroundColour,
		...cardBorderColour,
		...cardBorderRadiusPx,
		...cardBorderStyle,
		...cardBorderWidthPx,
		...cardBoxShadowStyle,
		...cardFont,
		...cardFontColour
	};

	// Generate header styles from context
	const headerStyles = {
		...cardHeaderFont,
		...cardHeaderFontColour
	};

	return (
		<>
			<Card className="action-card" onClick={() => setOpen(true)} style={cardStyles}>
				<FlexRowSpaced className="image-and-content">
					{/* Thumbnail */}
					{cardViewSettings?.thumbnailField && (
						<img
							src={thumbnailValue}
							alt=""
							style={{
								borderRadius: '16px',
								objectFit: 'cover',
								minWidth: '200px',
								width: '200px',
								height: '200px'
							}}
						/>
					)}

					{/* Card content */}
					<FlexColumnSmallSpaced className="card-content">
						<Typography
							variant="h5"
							style={{
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								...headerStyles
							}}
						>
							{titleValue}
						</Typography>
						<FlexColumnMiniSpaced>
							{cardFieldsToDisplay.map(({ column, value, cardField }) => (
								<Typography
									key={column.id}
									variant="body2"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}}
								>
									{!cardField.hideColumnName && <strong>{column.title}: </strong>}
									{renderFieldValue(value || '-', cardField.renderType)}
								</Typography>
							))}
						</FlexColumnMiniSpaced>
					</FlexColumnSmallSpaced>
				</FlexRowSpaced>
			</Card>

			<ResponsiveDrawer
				headerVariant="h5"
				placement={deviceType !== DeviceType.desktop ? 'bottom' : 'right'}
				title={drawerTitleValue}
				width={deviceType !== DeviceType.desktop ? '100%' : 500}
				style={deviceType !== DeviceType.desktop ? { height: '100%' } : {}}
				height={deviceType !== DeviceType.desktop ? '100%' : 'auto'}
				open={open}
				destroyOnClose
				onClose={() => setOpen(false)}
			>
				<Padded>
					<FlexColumnSpaced>
						{/* Table for ungrouped fields */}
						{groupedDrawerFields.ungroupedFields.length > 0 && (
							<TableContainer component={Paper}>
								<Table>
									<TableBody>
										{groupedDrawerFields.ungroupedFields.map(({ column, value, drawerField }) => (
											<TableRow key={column.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
												{!drawerField.hideColumnName && (
													<TableCell style={{ maxWidth: '10rem', fontWeight: 'bold' }}>{column.title}</TableCell>
												)}
												<TableCell
													align={drawerField.hideColumnName ? 'left' : 'right'}
													{...(drawerField.hideColumnName ? { colSpan: 2 } : {})}
												>
													{renderFieldValue(value || '-', drawerField?.renderType)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}

						{/* Separate table for each group */}
						{Object.entries(groupedDrawerFields.groups).map(([groupName, groupFields]) => (
							<TableContainer key={groupName} component={Paper}>
								<Table>
									<TableBody>
										{/* Group header row */}
										<TableRow>
											<TableCell
												colSpan={2}
												style={{
													fontWeight: 'bold',
													fontSize: '1.1em',
													backgroundColor: '#f5f5f5',
													borderTop: '2px solid #ddd'
												}}
											>
												{groupName}
											</TableCell>
										</TableRow>
										{/* Group fields */}
										{groupFields.map(({ column, value, drawerField }) => (
											<TableRow key={column.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
												{!drawerField.hideColumnName && (
													<TableCell style={{ maxWidth: '10rem', fontWeight: 'bold' }}>{column.title}</TableCell>
												)}
												<TableCell
													align={drawerField.hideColumnName ? 'left' : 'right'}
													{...(drawerField.hideColumnName ? { colSpan: 2 } : {})}
												>
													{renderFieldValue(value || '-', drawerField?.renderType)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						))}
					</FlexColumnSpaced>
				</Padded>
			</ResponsiveDrawer>
		</>
	);
}

export const ActionCardsWrapper = styled.div<{ $hasThumbnailField: boolean }>`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(${props => (props.$hasThumbnailField ? '35rem' : '27rem')}, 1fr));
	padding: 0.5rem;
	padding-top: 2rem;
	gap: 4rem;
	justify-content: center;
	width: 100%;
`;

const ResponsiveCardWrapper = styled.div<{ $hasThumbnailField: boolean; $deviceType: DeviceType }>`
	.action-card {
		border-radius: 16px;
		padding: 1rem;
		cursor: pointer;
		width: 100%;
		overflow: hidden;
		box-sizing: border-box;
	}
	${props =>
		props.$deviceType !== DeviceType.desktop &&
		`
		${ActionCardsWrapper} {
			padding: 0rem;
			padding-top: 1rem;
			gap: 1rem;
		}
		.action-card {
			width: 100%;
		}
	`}
	${props =>
		props.$deviceType === DeviceType.mobile &&
		`
		${ActionCardsWrapper} {
			grid-template-columns: 1fr;
		}
		.action-card {
			padding: 0;
		}
		.image-and-content {
			flex-direction: column;
			img {
				width: 100%!important;
				border-bottom-left-radius: 0px!important;
				border-bottom-right-radius: 0px!important;
			}
		}
		.card-content {
			padding: 0rem 1rem 1rem 1rem;
		}
	`}
`;
